import { useQueryClient } from "@tanstack/solid-query"
import { batch, createEffect, onCleanup } from "solid-js"
import { getFilename } from "@opencode/util/path"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal, type ServerCtx } from "@/runtime/server/runtime"
import { useSettingsSurface } from "@/settings/surface"
import { useSidebar } from "@/shell/sidebar/context"
import { useLayout, type LocalProject } from "@/shell/state/layout"
import { useTabs } from "@/shell/tabs/tabs"
import { useLanguage } from "@/runtime/i18n/language"
import { projectRenameError } from "./rename-error"

type RenameResult = Awaited<ReturnType<ServerCtx["sdk"]["api"]["project"]["rename"]>>
const pending = new WeakMap<ServerCtx, Map<string, Promise<void>>>()

function useProjectRenameState() {
  const global = useGlobal()
  const sidebar = useSidebar()
  const layout = useLayout()
  const tabs = useTabs()
  const settings = useSettingsSurface()
  const queryClient = useQueryClient()

  return (server: ServerConnection.Any, result: RenameResult) => {
    const context = global.ensureServerCtx(server)
    const key = ServerConnection.key(server)
    const token = JSON.stringify([result.previous, result.directory, result.project.time.updated])
    const active = pending.get(context) ?? new Map<string, Promise<void>>()
    pending.set(context, active)
    const existing = active.get(token)
    if (existing) return existing
    const promise = Promise.resolve().then(async () => {
      const name = getFilename(result.directory)
      const [update, updateDrafts] = await Promise.allSettled([
        context.sync.project.prepareRename(result.previous, result.directory, name, result.project),
        tabs.prepareProjectRename(key, result.previous, result.directory),
      ])
      batch(() => {
        if (update.status === "fulfilled") update.value()
        if (update.status === "rejected") console.error("Failed to restore renamed project metadata", update.reason)
        context.projects.rename(result.previous, result.directory)
        sidebar.renameProject(key, result.previous, result.directory, name)
        layout.home.renameProject(key, result.previous, result.directory)
        if (updateDrafts.status === "fulfilled") updateDrafts.value()
        if (updateDrafts.status === "rejected") {
          console.error("Failed to restore renamed project draft attachments", updateDrafts.reason)
          tabs.renameProject(key, result.previous, result.directory)
        }
        settings.renameProject(key, result.previous, result.directory)
      })
      void queryClient.invalidateQueries({ queryKey: ["home-sessions", server] })
    })
    active.set(token, promise)
    void promise.then(
      () => active.delete(token),
      () => active.delete(token),
    )
    return promise
  }
}

export function useRenameProject() {
  const global = useGlobal()
  const language = useLanguage()
  const apply = useProjectRenameState()
  return async (server: ServerConnection.Any, project: LocalProject, name: string) => {
    const result = await global
      .ensureServerCtx(server)
      .sdk.api.project.rename({ directory: project.worktree, name })
      .catch((error: unknown) => {
        throw projectRenameError(error, language.t)
      })
    await apply(server, result)
    return result
  }
}

export function ProjectRenameSync() {
  const global = useGlobal()
  const apply = useProjectRenameState()
  createEffect(() => {
    for (const server of global.servers.list()) {
      onCleanup(
        global.ensureServerCtx(server).sdk.event.on("project.renamed", (event) => {
          void apply(server, event.data)
        }),
      )
    }
  })
  return null
}
